import type { Messages } from './en';

/** Contenido de la fase 2: lugares nuevos (polideportivo, centro comercial, cuadras, enfermería, aula de música) y su gente. */
export const esPlaces: Pick<Messages, 'places' | 'talks2'> = {
  places: {
    zones: {
      gym: 'Gimnasio', pool: 'Piscina', nurse: 'Enfermería', music: 'Aula de música', cafe: 'Café Farolillo',
      giftShop: 'Tienda de regalos', dept: 'Grandes almacenes', restaurant: 'Restaurante', mall: 'Centro Comercial Farolillo',
      sportsCenter: 'Polideportivo', tennis: 'Pista de tenis', volley: 'Vóley playa', stable: 'Cuadras',
    },
    mapNames: { sports: 'Polideportivo', mall: 'Centro Comercial Farolillo' },
    objects: {
      nurseDesk: 'Mesa de la enfermera', pool: 'Piscina', gymNet: 'Red de vóley', tennisNet: 'Pista de tenis', volleyNet: 'Pista de vóley',
      fountain: 'Fuente', cafeCounter: 'Barra del café', giftCounter: 'Mostrador de regalos', deptCounter: 'Caja', restCounter: 'Mostrador del restaurante',
    },
    npc: {
      nurse: 'La enfermera Hsieh', musicTeacher: 'El profesor Kuo', lifeguard: 'La socorrista Ann', barista: 'Mei', giftClerk: 'Lulu',
      deptClerk: 'El señor Pan', waiter: 'Tom', stableKeeper: 'El tío Wu', horse: 'Caballo', pony: 'Poni',
    },
    items: {
      flowers: { name: 'Ramo de flores', desc: 'Un regalo que hace sonreír a cualquiera.' },
      scarf: { name: 'Bufanda', desc: 'Un regalo suave y calentito.' },
      keychain: { name: 'Llavero mono', desc: 'Un regalito para un amigo.' },
      toy: { name: 'Cochecito de juguete', desc: 'A los peques les encanta.' },
      sandwich: { name: 'Bocadillo', desc: 'Comer: +14 de energía' },
      cocoa: { name: 'Chocolate para llevar', desc: 'Beber: +12 de energía, +4 de ánimo' },
    },
    shops: {
      cafe: { title: '☕ Café Farolillo', hint: 'Bebidas calentitas y algo para picar.' },
      gift: { title: '🎁 Tienda de regalos', hint: 'Los regalos hacen feliz a la gente. ¡Dalos cuando charles con alguien!' },
      dept: { title: '👕 Grandes almacenes', hint: 'La ropa nueva te cambia el look al momento.' },
    },
    outfits: ['Uniforme del cole', 'Sudadera azul cielo', 'Vestido de cerezos', 'Rojo deportivo', 'Verde bosque'],
    outfitOn: (name: string) => `Llevas puesto: ${name}`,
    restaurant: {
      title: '🍜 Carta del restaurante',
      hint: 'Siéntate y come como es debido (+energía, +ánimo).',
      dishes: [
        { name: 'Sopa de fideos con ternera', desc: '+35 de energía' },
        { name: 'Empanadillas chinas (10)', desc: '+30 de energía, +5 de ánimo' },
        { name: 'Arroz frito con verduras', desc: '+28 de energía, +3 de estudio (¡comida para el cerebro!)' },
      ],
      eaten: (d: string) => `¡Ñam! Qué rico estaba: ${d}.`,
      full: 'Estás demasiado lleno/a para otra comida hoy.',
    },
    nurse: {
      text: 'La enfermería huele a sábanas limpias y a lavanda.',
      checkup: 'Hacerte una revisión (+energía)',
      checkupDone: 'La enfermera Hsieh te toma la temperatura. «¡Todo bien! Descansa un poquito y bebe agua».',
      bandage: 'Preguntar por primeros auxilios',
      bandageText: 'Si te haces un cortecito: lávalo con agua limpia, aprieta con suavidad para que deje de sangrar y tápalo con una tirita. ¡Si es profundo, avisa a un adulto!',
      already: 'Hoy ya te han hecho una revisión. ¡Cuídate!',
    },
    pool: {
      text: 'El agua brilla bajo la claraboya. La socorrista está vigilando.',
      swim: 'Nadar unos largos (−energía, +ánimo)',
      swimDone: '¡Chof! Has nadado diez largos. ¡Te sientes fuerte y como nuevo/a!',
      tired: 'Estás demasiado cansado/a para nadar con seguridad. ¡Descansa primero!',
      rules: 'Normas de la piscina',
      rulesText: '1. Dúchate antes de nadar. 2. No se corre por el borde. 3. Nada siempre acompañado/a. 4. Haz caso a la socorrista.',
    },
    rally: {
      tennis: 'Peloteo de tenis',
      volley: 'Entrenamiento de vóley',
      text: '¡Que viene la pelota! ¿Dónde caerá?',
      left: 'Izquierda', center: 'Centro', right: 'Derecha',
      hit: '¡Buen golpe!',
      miss: '¡Se te ha escapado! Mira bien la pelota…',
      done: (n: number) => `Fin del peloteo: ¡${n}/3 buenos golpes!`,
    },
    fountain: {
      text: 'Las monedas brillan en el fondo de la fuente.',
      wish: 'Tirar una moneda y pedir un deseo (1🪙)',
      wishDone: '¡Plinc! Pides un deseo para tu familia y tus amigos.',
      noCoin: 'No tienes monedas.',
    },
    horse: {
      text: 'Un caballo muy bueno te acaricia la mano con el hocico.',
      ride: 'Montar a caballo (¡con casco!)',
      dismount: 'Bajarte del caballo',
      riding: '¡Vas a caballo! Explora el cercado.',
      leave: 'El caballo se para en la valla. Los paseos son solo dentro del cercado.',
      feed: 'Darle una zanahoria (+ánimo)',
      fed: '¡Crunch, crunch! El caballo parece contentísimo.',
    },
    scenarios: {
      swim: 'Preguntar por las normas de la piscina', tennis: 'Aprender a sacar en tenis', horse: 'Montar a caballo con seguridad',
      clothes: 'Comprar ropa', music: 'Ir a clase de música', art: 'Hablar de tu dibujo',
    },
    gifts: {
      give: '🎁 Hacer un regalo',
      pick: '¿Qué regalo?',
      thanks: ['¡Oh! ¿Para mí? ¡Muchísimas gracias!', '¡Qué detallista eres! ¡Me encanta!', '¡Qué sorpresa tan bonita!'],
    },
  },
  /** Charla para los personajes nuevos (misma forma que `talks`). */
  talks2: {
    nurse: {
      greet: { morning: ['¡Buenos días! ¿Te encuentras bien?'], afternoon: ['Hola, cielo. ¿Necesitas algo?'], evening: ['¿Todavía por aquí? No te olvides de descansar.'] },
      topics: [
        {
          say: '¿Sabes por qué es tan importante dormir?',
          choices: [
            { t: 'Ayuda a mi cuerpo a crecer y a curarse.', reply: '¡Exacto! Y tu cerebro ordena todo lo que has aprendido.' },
            { t: '¿Cuántas horas debería dormir?', reply: 'Unas nueve o diez horas a tu edad.' },
            { t: '¿Y si mejor duermo en clase?', reply: '¡Jaja! Por la noche se duerme y en clase se aprende.' },
          ],
        },
        {
          say: 'Lavarse las manos es la forma más fácil de estar sano.',
          choices: [
            { t: '¡Me las lavaré antes de comer!', reply: 'Muy buena costumbre. ¡Veinte segundos con jabón!' },
            { t: '¿Por qué veinte segundos?', reply: 'Es lo que tarda el jabón en romper los microbios.' },
            { t: 'Cantaré una canción mientras me las lavo.', reply: '¡Perfecto! «Cumpleaños feliz» dos veces son unos veinte segundos.' },
          ],
        },
      ],
    },
    musicTeacher: {
      greet: { morning: ['¡Buenos días! Vamos a hacer música.'], afternoon: ['¡Ah, ha llegado un músico!'], evening: ['¿Una última canción antes de ir a casa?'] },
      topics: [
        {
          say: 'La música es como un idioma. Todo el mundo puede aprenderla.',
          choices: [
            { t: '¡Quiero aprender a tocar el piano!', reply: 'Estupendo. Empieza con una mano, despacito.' },
            { t: '¿Qué instrumento aprendió usted primero?', reply: '¡La flauta dulce! Chirriaba, pero me encantaba.' },
            { t: '¿Puedo aprender batería? ¿Bien fuerte?', reply: 'Jaja, sí. Primero el ritmo, luego el volumen.' },
          ],
        },
        {
          say: '¿Cantas cuando estás contento?',
          choices: [
            { t: '¡Sí! Así me siento todavía mejor.', reply: 'Dicen que la música duplica la felicidad.' },
            { t: '¿Cantar ayuda cuando estoy triste?', reply: 'Muchas veces, sí. Las canciones suaves calman el corazón.' },
            { t: 'Solo en la ducha.', reply: '¡La ducha tiene la mejor acústica!' },
          ],
        },
      ],
    },
    lifeguard: {
      greet: { morning: ['¿Nadamos por la mañana? ¡Primero, a calentar!'], afternoon: ['Hoy el agua está perfecta.'], evening: ['¡Últimos largos y a casa!'] },
      topics: [
        {
          say: 'Nunca nades solo, aunque nades muy bien.',
          choices: [
            { t: 'Entendido. ¡Siempre acompañado!', reply: 'Esa es la norma. Lo primero, la seguridad.' },
            { t: '¿Y si alguien necesita ayuda?', reply: 'Llama a gritos a un adulto o a un socorrista y lánzale algo que flote. No te tires al agua.' },
            { t: '¿Mi patito de goma cuenta como compañero?', reply: '¡Ja! Solo cuentan los compañeros humanos.' },
          ],
        },
      ],
    },
    barista: {
      greet: { morning: ['¡Buenos días! ¡Cruasanes recién hechos!'], afternoon: ['¡Bienvenido al Café Farolillo!'], evening: ['¡Buenas noches! Descafeinado para los mayores y chocolate para ti.'] },
      topics: [
        {
          say: '¡Kai, el de la tienda de la esquina, me ayudó a abrir este café!',
          choices: [
            { t: '¡Qué bonito! Aquí se está muy a gusto.', reply: '¡Gracias! Pusimos una plantita en cada mesa.' },
            { t: '¿Cuál es su bebida favorita?', reply: 'Chocolate caliente con un poquito de canela.' },
            { t: '¿Hay un menú secreto?', reply: 'Pss… pide el «Especial Farolillo». Lleva nubes de azúcar.' },
          ],
        },
      ],
    },
    giftClerk: {
      greet: { morning: ['¡Hola! ¿Buscas un regalo?'], afternoon: ['¡Bienvenido! Envolvemos todo gratis.'], evening: ['¿Un regalo de última hora? ¡Yo te ayudo!'] },
      topics: [
        {
          say: 'Los mejores regalos demuestran que has pensado de verdad en alguien.',
          choices: [
            { t: 'Elegiré algo que le guste de verdad.', reply: 'Ese es el secreto. Escucha lo que le encanta a la gente.' },
            { t: '¿Qué es lo que más compra la gente?', reply: 'Flores para las mamás y llaveros para los amigos.' },
            { t: '¿Puedo hacerme un regalo a mí mismo?', reply: 'Jaja, ¡a veces sí! Date un capricho.' },
          ],
        },
      ],
    },
    deptClerk: {
      greet: { morning: ['Buenos días. ¡Nueva temporada, nuevos estilos!'], afternoon: ['Bienvenido. ¡Pruébate lo que quieras!'], evening: ['Cerramos a las nueve.'] },
      topics: [
        {
          say: 'Ponte lo que te haga sentir cómodo y seguro.',
          choices: [
            { t: '¡Me gustan los colores alegres!', reply: '¡Pues el vestido de cerezos o el conjunto rojo deportivo!' },
            { t: '¿Qué se lleva este año?', reply: 'Las sudaderas. Suaves, calentitas y con bolsillos.' },
            { t: '¿Tienen capa de superhéroe?', reply: 'Por desgracia, no. De momento.' },
          ],
        },
      ],
    },
    waiter: {
      greet: { morning: ['Abrimos a las once, ¡pero pasa!'], afternoon: ['¿Mesa para uno?'], evening: ['¡Buenas noches! Hoy hay mucho jaleo.'] },
      topics: [
        {
          say: 'El caldo de nuestros fideos con ternera tarda seis horas en hacerse.',
          choices: [
            { t: '¡Hala, con razón está tan rico!', reply: '¡Gracias! El cocinero se pondrá muy orgulloso.' },
            { t: '¿Qué más está bueno aquí?', reply: 'Las empanadillas. Se hacen a mano cada mañana.' },
            { t: '¿Puedo tomar primero el postre?', reply: '¡Solo si prometes comerte luego la verdura!' },
          ],
        },
      ],
    },
    stableKeeper: {
      greet: { morning: ['¡Buenos días! Los caballos tienen hambre.'], afternoon: ['¿Quieres saludar a los caballos?'], evening: ['Es hora de que los caballos descansen.'] },
      topics: [
        {
          say: 'Acércate siempre a un caballo por un lado, para que te pueda ver.',
          choices: [
            { t: 'Iré con calma y con cuidado.', reply: 'Muy bien. Los caballos notan si estás nervioso.' },
            { t: '¿Qué les gusta comer a los caballos?', reply: 'Heno, zanahorias y manzanas. ¡Chocolate nunca!' },
            { t: '¿Los caballos hablan?', reply: '¡Con las orejas! Orejas hacia delante quiere decir que tienen curiosidad.' },
          ],
        },
      ],
    },
  },
};
